import { Place } from './place.model';
import { SupportTypes } from './support.model';
import { User } from './user.model';
import { Company } from './company.model';
import { DateType, FixedDate, IntervalDate } from './date.model';
import { DateUtils } from '../utils/date.utils';
import { TimeUtils } from '../utils/time.utils';
import { FixedTime, IntervalTime, TimeType } from './time.model';
import { ConcurrentResourceLocker } from '../realtime/concurrent-resource-registry/concurrent-resource-locker.model';
import { ConcurrentResource,
  ConcurrentResourceType } from '../realtime/concurrent-resource-registry/concurrent-resource.model';
import { Asset } from './asset.model';
import { PotentialAppointment } from './potential-appointment.model';
import { FreightForwarderEnum } from '../enum/freight-forwarder.enum';
import { TimeSlot } from '../ui-components/mp-time-slots/mp-time-slot-chip/time-slot.model';
import { DatedTimeSlots } from '../widgets/date-range-time-slot-group/date-range-time-slot-picker/date-range-time-slot-picker.model';

export class CreateOrUpdateAppointmentDTO {
  public id?: string;
  public tradeId?: string;
  public tradeRegularizationId?: string;
  public date?: FixedDate | IntervalDate;
  public time?: FixedTime | IntervalTime;
  public collectorId?: string;
  public googlePlaceId?: string;
  public donorId?: string;
  public asset?: Asset;
  public savedKilometers?: number;
  public redFlag?: boolean;
  public freightForwarding?: boolean;
  public comment?: string;

  public static mapFromAppointment(appointment: Appointment): CreateOrUpdateAppointmentDTO {
    let asset: Asset;
    if (appointment.asset && (appointment.asset.type || appointment.asset.quantity)) {
      asset = {
        type: appointment.asset.type,
        quantity: appointment.asset.quantity
      };
    }

    return {
      id: appointment.id,
      date: appointment.date,
      time: appointment.time,
      collectorId: appointment.collector === null ? null : appointment.collector?.id,
      googlePlaceId: appointment.place === null ? null : appointment.place?.provider.placeId,
      donorId: appointment.donor === null ? null : appointment.donor?.id,
      asset,
      redFlag: appointment.redFlag,
      freightForwarding: appointment.freightForwarding,
      comment: appointment.pinnedComment ? (appointment.pinnedComment.message || null) : undefined,
      tradeId: appointment.tradeId,
      tradeRegularizationId: appointment.tradeRegularizationId,
      savedKilometers: appointment.savedKilometers
    };
  }

  public static mapFromPotentialAppointment(potentialAppointment: PotentialAppointment): CreateOrUpdateAppointmentDTO {

    let date: FixedDate | IntervalDate;
    let time: IntervalTime;
    let comment: string;

    if (potentialAppointment.matchingDates?.length === 1) {
      const uniqueMatchingDate = potentialAppointment.matchingDates[0];
      date = uniqueMatchingDate.date;

      if (uniqueMatchingDate.timeSlots?.length === 1) {
        const uniqueTimeSlot = uniqueMatchingDate.timeSlots[0];
        time = {
          start: TimeUtils.mapTimeToHoursAndSeconds(uniqueTimeSlot.startTime),
          end: TimeUtils.mapTimeToHoursAndSeconds(uniqueTimeSlot.endTime),
          type: TimeType.INTERVAL_TIME
        };
      } else {
        comment = CreateOrUpdateAppointmentDTO.generateMultipleTimeSlotsComment(uniqueMatchingDate.timeSlots);
      }

    } else {
      comment = `Dates disponibles : ${CreateOrUpdateAppointmentDTO.generateMultipleDatesComment(potentialAppointment.matchingDates)}`;
    }

    return {
      date: date,
      time: time,
      collectorId: potentialAppointment.matchingSearch?.searchingCompany?.id,
      googlePlaceId: potentialAppointment.matchingOffer?.site?.place?.provider?.placeId,
      donorId: potentialAppointment.matchingOffer?.offeringCompany?.id,
      asset: potentialAppointment.matchingOffer?.asset,
      freightForwarding: potentialAppointment.matchingSearch?.freightForwarder === FreightForwarderEnum.MAGIC_PALLET,
      comment: comment || undefined
    };

  }

  private static generateMultipleDatesComment(datedTimeSlots: DatedTimeSlots[]) {
    let comment = '';

    datedTimeSlots.forEach((matchingDatedTimeSlot, index) => {

      if (DateUtils.isFixedDate(matchingDatedTimeSlot.date)) {
        const fixedDate = matchingDatedTimeSlot.date;
        comment += `le ${DateUtils.formatDateToDDMMYYYY(fixedDate.date)}`;
        comment += CreateOrUpdateAppointmentDTO.generateTimeSlotsComment(matchingDatedTimeSlot.timeSlots) ?? '';
      } else if (DateUtils.isIntervalDate(matchingDatedTimeSlot.date)) {
        const intervalDate = matchingDatedTimeSlot.date;
        comment += `du ${DateUtils.formatDateToDDMMYYYY(intervalDate.start)} au ${DateUtils.formatDateToDDMMYYYY(intervalDate.end)}`;
        comment += CreateOrUpdateAppointmentDTO.generateTimeSlotsComment(matchingDatedTimeSlot.timeSlots) ?? '';
      }

      if (index !== datedTimeSlots.length - 1) {
        comment += ', et ';
      }

    });

    return comment;
  }

  private static generateMultipleTimeSlotsComment(timeSlots: TimeSlot[]) {
    let comment = '';

    timeSlots.forEach((timeSlot, index) => {
      comment = `${CreateOrUpdateAppointmentDTO.mapTimeSlotToString(timeSlot)}`;
      if (index !== timeSlots.length - 1) {
        comment += ', et ';
      }
    });

    return comment;
  }

  private static generateTimeSlotsComment(timeSlots: TimeSlot[]) {
    let comment: string;

    if (timeSlots.length === 1) {
      const uniqueTimeSlot = timeSlots[0];
      comment = ` ${CreateOrUpdateAppointmentDTO.mapTimeSlotToString(uniqueTimeSlot)}`;
    } else {
      comment = ` ${CreateOrUpdateAppointmentDTO.generateMultipleTimeSlotsComment(timeSlots)}`;
    }

    return comment;
  }

  private static mapTimeSlotToString(timeSlot: TimeSlot) {
    return `entre ${TimeUtils.mapTimeToHoursAndSeconds(timeSlot.startTime)} et ${TimeUtils.mapTimeToHoursAndSeconds(timeSlot.endTime)}`;
  }

}

export const DefaultAppointmentAsset = {
  type: SupportTypes.SUPPORT_ASSET,
  quantity: 495
};

export abstract class AppointmentMetadata {
  isBeingAssigned?: boolean = false;
  isBeingTransformed?: boolean = false;
  lockedBy?: ConcurrentResourceLocker = null;
}


export enum AppointmentStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINALIZED = 'FINALIZED',
  EXPIRED = 'EXPIRED'
}

export class Appointment extends AppointmentMetadata {

  static DEFAULT_APPOINTMENT: Appointment = {
    status: AppointmentStatus.PENDING,
    asset: DefaultAppointmentAsset
  };

  public id?: string;
  public creationDate?: Date;
  public author?: User;
  public status?: AppointmentStatus;
  public date?: FixedDate | IntervalDate;
  public time?: FixedTime | IntervalTime;
  public collector?: Company;
  public place?: Place;
  public donor?: Company;
  public asset?: {
    type?: SupportTypes,
    quantity?: number
  };
  public redFlag?: boolean;
  public freightForwarding?: boolean;
  public pinnedComment?: {
    id?: number,
    message?: string,
    authorId?: number
  };
  public tradeId?: string;
  public tradeRegularizationId?: string;
  public deleted?: boolean = false;
  public savedKilometers?: number;

  public version?: number;

  public static mapToFlatObject(appointment: Appointment): any {
    return {
      tradeId: appointment?.tradeId,
      date: appointment?.date,
      time: appointment?.time,
      collector: appointment?.collector,
      place: appointment?.place,
      donor: appointment?.donor,
      support: appointment?.asset?.type || SupportTypes.SUPPORT_ASSET,
      quantity: appointment?.asset?.quantity,
      comment: appointment?.pinnedComment?.message || '',
      redFlag: appointment?.redFlag || false,
      freightForwarding: appointment?.freightForwarding || false,
      savedKilometers: appointment?.savedKilometers
    };
  }

  public static mapUpdatedFromForm(data: any, original: Appointment): Appointment {
    const appointment: Appointment = {};

    const isFixedDateChange =
      data.date && DateUtils.isFixedDate(data.date) &&
      !DateUtils.compareDates(data.date?.date, (original.date as FixedDate)?.date);

    const isIntervalDateChange =
      data.date?.start && data.date?.end &&
      DateUtils.isIntervalDate(data.date) &&
      (
        !DateUtils.compareDates(data.date?.start, (original.date as IntervalDate)?.start) ||
        !DateUtils.compareDates(data.date?.end, (original.date as IntervalDate)?.end)
      );

    const isDateRemoved = !data.date && original.date;

    // Handle date changes
    if (isFixedDateChange || isDateRemoved) {
      appointment.date = data.date ?
        {
          type: DateType.FIXED_DATE,
          date: data.date.date
        } :
        null;
    } else if (isIntervalDateChange || isDateRemoved) {
      appointment.date = data.date ?
        {
          type: DateType.INTERVAL_DATE,
          start: data.date.start,
          end: data.date.end
        } :
        null;
    }

    const isFixedTimeChange =
      data.time?.time && TimeUtils.isFixedTime(data.time) &&
      TimeUtils.mapTimeToHoursAndSeconds(data.time?.time) !== TimeUtils.mapTimeToHoursAndSeconds((original?.time as FixedTime)?.time);

    const isIntervalTimeChange =
      data.time?.start &&
      data.time?.end &&
      TimeUtils.isIntervalTime(data.time) &&
      (
        TimeUtils.mapTimeToHoursAndSeconds(data.time?.start) !== TimeUtils.mapTimeToHoursAndSeconds((original?.time as IntervalTime)?.start) ||
        TimeUtils.mapTimeToHoursAndSeconds(data.time?.end) !== TimeUtils.mapTimeToHoursAndSeconds((original?.time as IntervalTime)?.end)
      );

    const isTimeRemoved = !data.time && original.time;

    // Handle time changes
    if (isFixedTimeChange || isTimeRemoved) {
      appointment.time = data.time ?
        {
          type: TimeType.FIXED_TIME,
          time: data.time.time
        } :
        null;
    } else if (isIntervalTimeChange || isTimeRemoved) {
      appointment.time = data.time ?
        {
          type: TimeType.INTERVAL_TIME,
          start: data.time.start,
          end: data.time.end
        } :
        null;
    }

    const hasCollectorChanged = data.collector && data.collector.id !== original.collector?.id;
    const isCollectorRemoved = !data.collector && original.collector;

    // Handle collector changes
    if (hasCollectorChanged || isCollectorRemoved) {
      appointment.collector = data.collector || null;
    }

    const hasDonorChanged = data.donor && data.donor.id !== original.donor?.id;
    const isDonorRemoved = !data.donor && original.donor;

    // Handle donor changes
    if (hasDonorChanged || isDonorRemoved) {
      appointment.donor = data.donor || null;
    }

    const hasPlaceChanged = data.place && data.place.provider.placeId !== original.place?.provider?.placeId;
    const isPlaceRemoved = !data.place && original.place;
    // Handle place changes
    if (hasPlaceChanged || isPlaceRemoved) {
      appointment.place = data.place || null;
    }

    // Handle asset changes
    if (data.quantity !== original.asset?.quantity || data.support !== original.asset?.type) {
      appointment.asset = {
        type: data.support,
        quantity: data.quantity || null
      };
    }

    // Handle red flag changes
    if (data.redFlag !== original.redFlag && (data.redFlag || original.redFlag)) {
      appointment.redFlag = data.redFlag;
    }

    // Handle freight forwarding changes
    if (data.freightForwarding !== original.freightForwarding && (data.freightForwarding || original.freightForwarding)) {
      appointment.freightForwarding = data.freightForwarding;
    }

    // Handle pinned comment changes
    if (data.comment !== original.pinnedComment?.message && (data.comment || original.pinnedComment)) {
      appointment.pinnedComment = { message: data.comment };
    }

    if (data.savedKilometers !== original.savedKilometers && (data.savedKilometers || original.savedKilometers)) {
      appointment.savedKilometers = data.savedKilometers || null;
    }

    return appointment;
  }

  public static trackById(index: number, appointment: Appointment) {
    return appointment.id !== undefined ? appointment.id : index;
  }

  public static mapToConcurrentResource(appointment: Appointment): ConcurrentResource {
    return { uuid: appointment.id, type: ConcurrentResourceType.APPOINTMENT };
  };
}

export enum AppointmentFilters {
  COLLECTOR = 'collectorIds',
  DONOR = 'donorIds',
  AUTHOR = 'authorIds',
  STATUS = 'status',
  SUPPORT_TYPE = 'assetTypes',
  RED_FLAGGED = 'redFlagged',
  FREIGHT_FORWARDING = 'freightForwarding',
  EMPTY_FIELDS = 'emptyFields',
  PLACE = 'place',
  DATE = 'date'
}

export enum AppointmentEmptyFieldValues {
  DATE = 'DATE',
  DONOR = 'DONOR',
  PLACE = 'PLACE',
  QUANTITY = 'QUANTITY'
}
